import { useCallback } from 'react'
import { useStartEnrollment, useSubmitSponsoredEnrollmentStep } from '@/api/enrollment/start'
import { useNavigate } from '@/router/navigation'
import { useEnrollmentStore } from '@/stores'
import { ENROLLMENT_COMMON_MESSAGES } from '../../../../constants/enrollmentContent'
import { SELF_ENROLLMENT_STEPS } from '../../../selfEnrollmentFlow/utils/steps'
import type { ApplicationFormState } from '../../types'
import { getRegisterErrorMessage } from '../../utils'
import { persistAddressFields } from '../../utils/storage'
import {
  buildApplicationRegisterPayload,
  persistRegistrationResult,
  toPersistedAddressParts
} from '../../utils/registration'

type AddressParts = {
  city: string
  state: string
  zipCode: string
}

type UseApplicationFormSubmitHandlersArgs = {
  canRegister: boolean
  getAddressParts: () => AddressParts
  state: ApplicationFormState
}

export function useApplicationFormSubmitHandlers({
  canRegister,
  getAddressParts,
  state
}: UseApplicationFormSubmitHandlersArgs) {
  const navigate = useNavigate()
  const startEnrollmentMutation = useStartEnrollment()
  const submitSponsoredEnrollmentStepMutation = useSubmitSponsoredEnrollmentStep()
  const storedSessionToken = useEnrollmentStore(store => store.sessionToken)
  const setSessionToken = useEnrollmentStore(store => store.setSessionToken)
  const setCurrentStep = useEnrollmentStore(store => store.setCurrentStep)
  const updateFormData = useEnrollmentStore(store => store.updateFormData)

  const handleRegister = useCallback(async () => {
    if (!canRegister) return

    state.setIsSubmitting(true)
    const addressComponents = getAddressParts()
    const enrollmentSessionToken = storedSessionToken?.trim() ?? ''

    try {
      const payload = buildApplicationRegisterPayload({
        addressParts: addressComponents,
        state
      })
      const data = enrollmentSessionToken
        ? await submitSponsoredEnrollmentStepMutation.mutateAsync({
            sessionToken: enrollmentSessionToken,
            data: payload
          })
        : await startEnrollmentMutation.mutateAsync(payload)

      if (data) {
        persistRegistrationResult({
          addressParts: addressComponents,
          data,
          setCurrentStep,
          setSessionToken,
          state,
          updateFormData
        })
        persistAddressFields(
          toPersistedAddressParts(data.addressLine1 ?? state.formData.addressLine1, addressComponents)
        )
      }

      navigate('/self-enroll', {
        state: {
          startStep: SELF_ENROLLMENT_STEPS.ELIGIBILITY,
          stayOnApplication: false
        }
      })
    } catch (error) {
      state.setSubmitError(
        getRegisterErrorMessage(error, ENROLLMENT_COMMON_MESSAGES.startEnrollmentFailed)
      )
    } finally {
      state.setIsSubmitting(false)
    }
  }, [
    canRegister,
    getAddressParts,
    navigate,
    startEnrollmentMutation,
    submitSponsoredEnrollmentStepMutation,
    setCurrentStep,
    setSessionToken,
    state,
    storedSessionToken,
    updateFormData
  ])

  return {
    handleRegister
  }
}
